import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import RichTextEditor from './RichTextEditor';

const ArticlesManager = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    date: '',
    summary: '',
    content: '',
    image_url: '',
    category: 'thought-leadership',
    published: true
  });
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/articles`);
      const data = await response.json();
      if (data.success) {
        setArticles(data.articles);
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = editingId
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/articles/${editingId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/articles`;
      
      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success) {
        await fetchArticles();
        resetForm();
      } else {
        // Keep the form open and the typed content intact so nothing is lost
        window.alert(
          (data && data.detail) ||
          `Could not save the article (status ${response.status}). Your text has NOT been lost — please try saving again.`
        );
      }
    } catch (error) {
      console.error('Error saving article:', error);
      window.alert('Network error while saving. Your text has NOT been lost — please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (article) => {
    setFormData({
      title: article.title,
      author: article.author,
      date: article.date,
      summary: article.summary,
      content: article.content,
      image_url: article.image_url || '',
      category: article.category,
      published: article.published
    });
    setEditingId(article.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;

    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/articles/${id}`, {
        method: 'DELETE'
      });
      await fetchArticles();
    } catch (error) {
      console.error('Error deleting article:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      author: '',
      date: '',
      summary: '',
      content: '',
      image_url: '',
      category: 'thought-leadership',
      published: true
    });
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: '700', color: '#111111' }}>
          Articles Manager
        </h1>
        <button
          onClick={() => setShowForm(!showForm)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            backgroundColor: '#111111',
            color: '#F4F1E8',
            border: 'none',
            borderRadius: '4px',
            fontSize: '0.95rem',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          {showForm ? <X size={20} /> : <Plus size={20} />}
          {showForm ? 'Cancel' : 'Add Article'}
        </button>
      </div>

      {showForm && (
        <div style={{
          backgroundColor: '#FFFFFF',
          padding: '32px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          marginBottom: '24px'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '24px', color: '#111111' }}>
            {editingId ? 'Edit Article' : 'New Article'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', marginBottom: '8px', color: '#111111' }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: '1px solid #E0E0E0',
                    borderRadius: '4px',
                    fontSize: '0.95rem'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', marginBottom: '8px', color: '#111111' }}>
                  Author *
                </label>
                <select
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: '1px solid #E0E0E0',
                    borderRadius: '4px',
                    fontSize: '0.95rem'
                  }}
                >
                  <option value="">Select author</option>
                  <option value="Craig Kimball">Craig Kimball</option>
                  <option value="Nadir Khan">Nadir Khan</option>
                  <option value="Craig Kimball & Nadir Khan">Both Authors</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', marginBottom: '8px', color: '#111111' }}>
                  Date *
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: '1px solid #E0E0E0',
                    borderRadius: '4px',
                    fontSize: '0.95rem'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', marginBottom: '8px', color: '#111111' }}>
                  Image URL
                </label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://..."
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: '1px solid #E0E0E0',
                    borderRadius: '4px',
                    fontSize: '0.95rem'
                  }}
                />
              </div>
            </div>
            <div style={{ marginTop: '20px' }}>
              <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', marginBottom: '8px', color: '#111111' }}>
                Summary *
              </label>
              <textarea
                value={formData.summary}
                onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                required
                rows="3"
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: '1px solid #E0E0E0',
                  borderRadius: '4px',
                  fontSize: '0.95rem',
                  fontFamily: 'var(--font-body)',
                  resize: 'vertical'
                }}
              />
            </div>
            <div style={{ marginTop: '20px' }}>
              <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', marginBottom: '8px', color: '#111111' }}>
                Content *
              </label>
              <RichTextEditor
                value={formData.content}
                onChange={(html) => setFormData({ ...formData, content: html })}
              />
            </div>
            <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <input
                type="checkbox"
                checked={formData.published}
                onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                style={{ width: '18px', height: '18px' }}
              />
              <label style={{ fontSize: '0.95rem', color: '#111111' }}>
                Published
              </label>
            </div>
            <div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  backgroundColor: loading ? '#999999' : '#111111',
                  color: '#F4F1E8',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                <Save size={18} />
                {loading ? 'Saving...' : 'Save Article'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  color: '#666666',
                  border: '1px solid #E0E0E0',
                  borderRadius: '4px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div style={{ backgroundColor: '#FFFFFF', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}>
        {loading && !showForm ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#666666' }}>Loading articles...</div>
        ) : articles.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#666666' }}>
            No articles yet. Click "Add Article" to create one.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #E0E0E0' }}>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '0.85rem', fontWeight: '600', color: '#666666', textTransform: 'uppercase' }}>Title</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '0.85rem', fontWeight: '600', color: '#666666', textTransform: 'uppercase' }}>Author</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '0.85rem', fontWeight: '600', color: '#666666', textTransform: 'uppercase' }}>Date</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '0.85rem', fontWeight: '600', color: '#666666', textTransform: 'uppercase' }}>Status</th>
                  <th style={{ padding: '16px', textAlign: 'right', fontSize: '0.85rem', fontWeight: '600', color: '#666666', textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {articles.map((article) => (
                  <tr key={article.id} style={{ borderBottom: '1px solid #F0F0F0' }}>
                    <td style={{ padding: '16px', fontSize: '0.95rem', color: '#111111', fontWeight: '500' }}>
                      {article.title}
                    </td>
                    <td style={{ padding: '16px', fontSize: '0.9rem', color: '#666666' }}>
                      {article.author}
                    </td>
                    <td style={{ padding: '16px', fontSize: '0.9rem', color: '#666666' }}>
                      {new Date(article.date).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '16px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        backgroundColor: article.published ? '#e6f7e6' : '#ffe6e6',
                        color: article.published ? '#00cc00' : '#cc0000'
                      }}>
                        {article.published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right' }}>
                      <button
                        onClick={() => handleEdit(article)}
                        style={{
                          padding: '8px',
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#111111',
                          marginRight: '8px'
                        }}
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(article.id)}
                        style={{
                          padding: '8px',
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#cc0000'
                        }}
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticlesManager;
